import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Activity, AlertTriangle, ChevronDown, ChevronRight, ChevronUp, Search } from 'lucide-react';
import { healthApi } from '../api';
import HealthMetricTrendChart, { HealthTrendPoint } from './HealthMetricTrendChart';
import { WellnessTabBar, WellnessWorkspace } from './WellnessCenter';

interface Report {
  id: number;
  title: string;
  file_name: string;
  file_url: string;
  report_date: string;
  status: string;
  error_message: string;
  metrics_count: number;
  metrics_total?: number;
  created_at: string;
}

interface Metric {
  id: number;
  code?: string;
  category: string;
  name: string;
  name_en: string;
  value: string;
  numeric_value: number | null;
  unit: string;
  reference_range: string;
  status: string;
  report_date: string;
  detected?: boolean;
  sort_order?: number;
}

interface MetricRecord {
  id: number;
  report_id: number | null;
  code: string;
  name: string;
  name_en: string;
  value: string;
  numeric_value: number | null;
  unit: string;
  reference_range: string;
  status: string;
  report_date: string;
  detected: boolean;
}

interface CatalogItem {
  code: string;
  name: string;
  name_en: string;
  category: string;
  category_label: string;
  unit: string;
  reference_range: string;
  latest: MetricRecord;
  record_count: number;
  chart_point_count: number;
  records: MetricRecord[];
  trend: HealthTrendPoint[];
  chart_trend: HealthTrendPoint[];
}

interface HealthManagerProps {
  onClose: () => void;
  initialTab?: TabKey;
  onNavigateWorkspace?: (w: WellnessWorkspace) => void;
}

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  normal: { label: '正常', color: '#10b981', bg: 'rgba(16,185,129,0.15)' },
  high: { label: '偏高', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  low: { label: '偏低', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
  abnormal: { label: '异常', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
  unknown: { label: '—', color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
};

const REPORT_STATUS_MAP: Record<string, { label: string; color: string }> = {
  pending: { label: '待解析', color: '#a78bfa' },
  processing: { label: '解析中…', color: '#f59e0b' },
  done: { label: '已完成', color: '#10b981' },
  failed: { label: '失败', color: '#ef4444' },
};

// 核心关注指标列表（与 Django web 端对齐）
const CORE_METRIC_CODES = ['weight', 'bmi', 'sbp', 'dbp', 'fbg', 'tc', 'tg'];

type TabKey = 'overview' | 'metrics' | 'reports';

// ---- 辅助：解析参考范围字符串 → {low, high} ----
function parseRefRange(ref: string): { low: number; high: number } | null {
  if (!ref) return null;
  const s = ref.replace(/[～~—–]/g, '-').trim();
  const m = s.match(/([\d.]+)\s*-\s*([\d.]+)/);
  if (m) return { low: parseFloat(m[1]), high: parseFloat(m[2]) };
  return null;
}

// ---- 辅助：计算值在参考范围内的百分比（0-100） ----
function refFillPct(val: number | null, ref: string): number | null {
  if (val == null) return null;
  const r = parseRefRange(ref);
  if (!r) return null;
  const range = r.high - r.low;
  if (range <= 0) return 50;
  return Math.min(100, Math.max(0, ((val - r.low) / range) * 100));
}

// ---- 辅助：判断是否异常 ----
function isAbnormal(status: string): boolean {
  return status === 'high' || status === 'low' || status === 'abnormal';
}

// ---- 辅助：天数差 ----
function daysSince(dateStr: string): number {
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((now.getTime() - d.getTime()) / 86400000);
}

// ---- SVG 环形进度组件 ----
const RingChart: React.FC<{ total: number; abnormal: number }> = ({ total, abnormal }) => {
  const R = 45;
  const CIRC = 2 * Math.PI * R; // ≈ 283
  const normalCount = total - abnormal;
  const normalArc = total > 0 ? (normalCount / total) * CIRC : CIRC;
  const abnormalArc = total > 0 ? (abnormal / total) * CIRC : 0;
  const hasData = total > 0;

  // stroke-dashoffset: 从12点钟方向开始 = -CIRC/4
  const offset = -(CIRC / 4);

  return (
    <svg viewBox="0 0 100 100" style={{ width: 88, height: 88 }}>
      {/* 背景环 */}
      <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(148,163,184,0.1)" strokeWidth="10" />
      {hasData && abnormal > 0 && (
        /* 异常弧（红，从12点顺时针先画） */
        <circle
          cx="50" cy="50" r={R} fill="none"
          stroke="#ef4444" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={`${abnormalArc} ${CIRC - abnormalArc}`}
          strokeDashoffset={offset}
        />
      )}
      {hasData && (
        /* 正常弧（绿，紧跟异常弧） */
        <circle
          cx="50" cy="50" r={R} fill="none"
          stroke="#10b981" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={`${normalArc} ${CIRC - normalArc}`}
          strokeDashoffset={offset - abnormalArc}
        />
      )}
      {/* 中心文字 */}
      {hasData ? (
        <>
          <text x="50" y="46" textAnchor="middle" fill="#f1f5f9" fontSize="18" fontWeight="900">
            {abnormal > 0 ? `${Math.round((abnormal / total) * 100)}%` : '100%'}
          </text>
          <text x="50" y="60" textAnchor="middle" fill="rgba(148,163,184,0.7)" fontSize="9">
            {abnormal > 0 ? '异常率' : '全部正常'}
          </text>
        </>
      ) : (
        <text x="50" y="54" textAnchor="middle" fill="rgba(148,163,184,0.5)" fontSize="9">
          暂无数据
        </text>
      )}
    </svg>
  );
};

const HealthManager: React.FC<HealthManagerProps> = ({ onClose, initialTab = 'overview', onNavigateWorkspace }) => {
  // 默认 Tab 改为 overview
  const [tab, setTab] = useState<TabKey>(initialTab);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<Report[]>([]);
  const [summary, setSummary] = useState<any>(null);

  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [catalogGrouped, setCatalogGrouped] = useState<Record<string, CatalogItem[]>>({});
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [metricSearch, setMetricSearch] = useState('');
  const [abnormalOnly, setAbnormalOnly] = useState(false);
  const [showMoreMetrics, setShowMoreMetrics] = useState(false);

  const [selectedMetric, setSelectedMetric] = useState<CatalogItem | null>(null);
  const [metricDetailLoading, setMetricDetailLoading] = useState(false);

  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reportDetail, setReportDetail] = useState<{ grouped: Record<string, Metric[]>; metrics: Metric[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDate, setUploadDate] = useState(() => {
    const d = new Date();
    const p = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  });
  const [uploadTitle, setUploadTitle] = useState('');
  const [msg, setMsg] = useState<{ text: string; type: 'ok' | 'err' } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTab(initialTab);
    setSelectedMetric(null);
    setSelectedReport(null);
    setReportDetail(null);
  }, [initialTab]);

  const loadReports = useCallback(async () => {
    try {
      const res = await healthApi.getReports();
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const res = await healthApi.getMetricsSummary();
      setSummary(res.data);
    } catch { /* ignore */ }
  }, []);

  const loadCatalog = useCallback(async () => {
    try {
      setCatalogLoading(true);
      const res = await healthApi.getMetricsCatalog();
      const items = res.data?.items?.length ? res.data.items : res.data?.available_items || [];
      const grouped = Object.keys(res.data?.grouped || {}).length ? res.data.grouped : res.data?.available_grouped || {};
      setCatalog(items);
      setCatalogGrouped(grouped);
    } catch {
      setCatalog([]);
      setCatalogGrouped({});
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.allSettled([loadReports(), loadSummary(), loadCatalog()]);
  }, [loadReports, loadSummary, loadCatalog]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refreshAll();
      setLoading(false);
    })();
  }, [refreshAll]);

  const openMetricDetail = async (item: CatalogItem) => {
    setSelectedMetric(item);
    setMetricDetailLoading(true);
    try {
      const res = await healthApi.getMetricByCode(item.code);
      setSelectedMetric(res.data as CatalogItem);
    } catch {
      /* keep list item */
    } finally {
      setMetricDetailLoading(false);
    }
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setMsg({ text: '请选择文件', type: 'err' });
      return;
    }
    setUploading(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('report_date', uploadDate);
      if (uploadTitle.trim()) fd.append('title', uploadTitle.trim());
      await healthApi.uploadReport(fd);
      setMsg({ text: '上传成功，AI 正在解析中…', type: 'ok' });
      if (fileRef.current) fileRef.current.value = '';
      setUploadTitle('');
      setUploadOpen(false);
      await refreshAll();
      setTab('reports');
    } catch (e: any) {
      setMsg({ text: e?.response?.data?.detail || '上传失败', type: 'err' });
    } finally {
      setUploading(false);
    }
  };

  const openReportDetail = async (r: Report) => {
    setSelectedReport(r);
    setDetailLoading(true);
    try {
      const res = await healthApi.getReportDetail(r.id);
      setReportDetail({ grouped: res.data.grouped || {}, metrics: res.data.metrics || [] });
    } catch {
      setReportDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确定删除该体检报告及其所有指标？')) return;
    try {
      await healthApi.deleteReport(id);
      setReports((prev) => prev.filter((r) => r.id !== id));
      if (selectedReport?.id === id) {
        setSelectedReport(null);
        setReportDetail(null);
      }
      await refreshAll();
    } catch { /* ignore */ }
  };

  const handleReparse = async (id: number) => {
    try {
      await healthApi.reparseReport(id);
      await loadReports();
      setMsg({ text: '重新解析已触发', type: 'ok' });
    } catch { /* ignore */ }
  };

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const hasPending = reports.some((r) => r.status === 'pending' || r.status === 'processing');
    if (hasPending) {
      pollTimer.current = setInterval(() => refreshAll(), 5000);
    }
    return () => {
      if (pollTimer.current != null) clearInterval(pollTimer.current);
    };
  }, [reports, refreshAll]);

  // 过滤逻辑：搜索 + 只看异常
  const filteredCatalog = catalog.filter((item) => {
    const matchSearch = !metricSearch.trim() || (() => {
      const q = metricSearch.trim().toLowerCase();
      return (
        item.name.toLowerCase().includes(q) ||
        item.name_en.toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q)
      );
    })();
    const matchAbnormal = !abnormalOnly || isAbnormal(item.latest?.status || 'unknown');
    return matchSearch && matchAbnormal;
  });

  const filteredGrouped = Object.entries(catalogGrouped).reduce<Record<string, CatalogItem[]>>(
    (acc, [cat, items]) => {
      const filtered = items.filter((item) => filteredCatalog.some((f) => f.code === item.code));
      if (filtered.length) acc[cat] = filtered;
      return acc;
    },
    {}
  );

  // 核心指标（从 catalog 中取 CORE_METRIC_CODES）
  const coreMetrics = CORE_METRIC_CODES
    .map((code) => catalog.find((c) => c.code === code))
    .filter(Boolean) as CatalogItem[];

  const priorityMetrics = [...catalog]
    .sort((a, b) => {
      const aCoreIndex = CORE_METRIC_CODES.indexOf(a.code);
      const bCoreIndex = CORE_METRIC_CODES.indexOf(b.code);
      const aIsCore = aCoreIndex >= 0 ? 1 : 0;
      const bIsCore = bCoreIndex >= 0 ? 1 : 0;
      if (aIsCore !== bIsCore) return bIsCore - aIsCore;
      const aAbnormal = isAbnormal(a.latest?.status || 'unknown') ? 1 : 0;
      const bAbnormal = isAbnormal(b.latest?.status || 'unknown') ? 1 : 0;
      if (aAbnormal !== bAbnormal) return bAbnormal - aAbnormal;
      if (aIsCore && bIsCore) return aCoreIndex - bCoreIndex;
      return 0;
    })
    .filter((item, index, items) => items.findIndex((candidate) => candidate.code === item.code) === index)
    .slice(0, 4);

  const priorityCodes = new Set(priorityMetrics.map((item) => item.code));
  const secondaryCatalog = filteredCatalog.filter((item) => !priorityCodes.has(item.code));
  const secondaryGrouped = Object.entries(catalogGrouped).reduce<Record<string, CatalogItem[]>>(
    (acc, [cat, items]) => {
      const filtered = items.filter((item) => secondaryCatalog.some((candidate) => candidate.code === item.code));
      if (filtered.length) acc[cat] = filtered;
      return acc;
    },
    {}
  );
  const abnormalCount = catalog.filter((item) => isAbnormal(item.latest?.status || 'unknown')).length;
  const normalCount = catalog.filter((item) => item.latest?.status === 'normal').length;
  const shouldShowSecondary = showMoreMetrics || Boolean(metricSearch.trim()) || abnormalOnly;

  // ===================== 指标详情页（全屏替换） =====================
  if (selectedMetric) {
    const st = STATUS_MAP[selectedMetric.latest?.status || 'unknown'] || STATUS_MAP.unknown;
    const chartData =
      selectedMetric.chart_trend?.length > 0
        ? selectedMetric.chart_trend
        : selectedMetric.trend || [];

    return (
      <div className="hm-root hm-metric-detail-root">
        <div className="hm-header">
          <button className="hm-back" onClick={() => setSelectedMetric(null)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <h1 className="hm-page-title hm-truncate">{selectedMetric.name}</h1>
          <div style={{ width: 34, flexShrink: 0 }} />
        </div>

        <div className="hm-body hm-metric-detail-body">
          {metricDetailLoading ? (
            <div className="hm-loading">
              <div className="hm-spinner" />
              <p>加载中…</p>
            </div>
          ) : (
            <>
              <div className="hm-metric-hero">
                <div className="hm-metric-hero-top">
                  <div>
                    <div className="hm-metric-hero-name">{selectedMetric.name}</div>
                    {selectedMetric.name_en && (
                      <div className="hm-metric-hero-en">{selectedMetric.name_en}</div>
                    )}
                    <div className="hm-metric-hero-code">#{selectedMetric.code}</div>
                  </div>
                  <div className="hm-metric-hero-right">
                    <div className="hm-metric-hero-value">{selectedMetric.latest?.value}</div>
                    {selectedMetric.unit && (
                      <div className="hm-metric-hero-unit">{selectedMetric.unit}</div>
                    )}
                    <span className="hm-metric-badge" style={{ color: st.color, background: st.bg }}>
                      {st.label}
                    </span>
                  </div>
                </div>
                {/* 参考范围进度条 */}
                {selectedMetric.latest?.numeric_value != null && selectedMetric.reference_range && (() => {
                  const pct = refFillPct(selectedMetric.latest.numeric_value, selectedMetric.reference_range);
                  if (pct == null) return null;
                  return (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(148,163,184,0.6)', marginBottom: 4 }}>
                        <span>参考范围</span>
                        <span>{selectedMetric.reference_range}</span>
                      </div>
                      <div style={{ width: '100%', height: 6, background: 'rgba(148,163,184,0.1)', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: st.color, borderRadius: 999, minWidth: 4, opacity: 0.8 }} />
                      </div>
                    </div>
                  );
                })()}
                <div className="hm-metric-hero-meta">
                  <span>最近: {selectedMetric.latest?.report_date}</span>
                  <span>共 {selectedMetric.record_count} 次记录</span>
                  {selectedMetric.reference_range && (
                    <span>参考: {selectedMetric.reference_range}</span>
                  )}
                </div>
              </div>

              <div className="hm-chart-card">
                <h3 className="hm-section-title">📈 历史趋势</h3>
                <HealthMetricTrendChart
                  data={chartData}
                  unit={selectedMetric.unit}
                  referenceRange={selectedMetric.reference_range}
                  height={220}
                />
              </div>

              <div className="hm-section">
                <h3 className="hm-section-title">📋 全部存储记录</h3>
                <p className="hm-records-hint">每次体检解析后，该指标单独存为一条记录</p>
                <div className="hm-records-list">
                  {[...(selectedMetric.records || [])].reverse().map((rec, idx, arr) => {
                    const rst = STATUS_MAP[rec.status] || STATUS_MAP.unknown;
                    // 与上一条比较 delta
                    const prev = arr[idx + 1];
                    let delta: string | null = null;
                    if (prev && rec.numeric_value != null && prev.numeric_value != null) {
                      const diff = rec.numeric_value - prev.numeric_value;
                      const sign = diff > 0 ? '+' : '';
                      delta = `${sign}${diff.toFixed(1)}`;
                    }
                    return (
                      <div key={rec.id} className="hm-record-card">
                        <div className="hm-record-top-row">
                          <div className="hm-record-date">📅 {rec.report_date}</div>
                          {delta && (
                            <span className="hm-record-delta" style={{
                              color: delta.startsWith('+') ? '#f59e0b' : '#3b82f6',
                            }}>
                              {delta.startsWith('+') ? '↑' : '↓'} {delta.replace(/^[+-]/, '')}
                            </span>
                          )}
                        </div>
                        <div className="hm-record-val">
                          {rec.value}{rec.unit ? ` ${rec.unit}` : ''}
                        </div>
                        <span className="hm-metric-badge" style={{ color: rst.color, background: rst.bg }}>
                          {rst.label}
                        </span>
                        {rec.reference_range && (
                          <div className="hm-record-ref">参考 {rec.reference_range}</div>
                        )}
                        {rec.numeric_value != null && rec.reference_range && (() => {
                          const pct = refFillPct(rec.numeric_value, rec.reference_range);
                          if (pct == null) return null;
                          return (
                            <div style={{ width: '100%', height: 4, background: 'rgba(148,163,184,0.1)', borderRadius: 999, overflow: 'hidden', marginTop: 6 }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: rst.color, borderRadius: 999, minWidth: 3, opacity: 0.7 }} />
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
        <HmStyles />
      </div>
    );
  }

  // ===================== 主页面 =====================

  const activeWellnessTab: WellnessWorkspace = tab === 'reports' ? 'reports' : 'metrics';
  const handleWellnessNav = (w: WellnessWorkspace) => {
    if (w === 'metrics' || w === 'reports') {
      setTab(w);
    } else {
      onNavigateWorkspace?.(w);
    }
  };

  return (
    <div className="hm-root">
      {/* Header */}
      <div className="hm-header">
        <button className="hm-back" onClick={onClose}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg></button>
        <h1 className="hm-page-title">🏥 健康管理</h1>
        {/* 浮动上传按钮（header 右侧） */}
        <button
          className="hm-fab-upload"
          onClick={() => setUploadOpen(true)}
          title="上传体检报告"
        >
          ＋
        </button>
      </div>

      {onNavigateWorkspace && (
        <WellnessTabBar active={activeWellnessTab} onNavigate={handleWellnessNav} />
      )}

      {msg && <div className={`hm-toast ${msg.type}`}>{msg.text}</div>}

      <div className={`hm-body${tab === 'metrics' ? ' hm-body-wide' : ''}`}>
        {loading ? (
          <div className="hm-loading">
            <div className="hm-spinner" />
            <p>加载中…</p>
          </div>
        ) : (
          <>
            {/* ================== 概览 Tab ================== */}
            {tab === 'overview' && (
              <div className="hm-overview">
                {!summary?.latest_report ? (
                  /* 空状态 onboarding */
                  <div className="hm-onboarding-card">
                    <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 12 }}>🏥</div>
                    <h2 style={{ fontSize: 18, fontWeight: 900, textAlign: 'center', margin: '0 0 20px' }}>
                      开始追踪你的健康
                    </h2>
                    <div className="hm-onboarding-steps">
                      {[
                        '上传体检报告（JPG / PDF / TXT）',
                        'AI 自动识别血常规、血脂等 80+ 项指标',
                        '查看历史趋势，了解你的身体变化',
                      ].map((text, i) => (
                        <div key={i} className="hm-onboarding-step">
                          <span className="hm-step-num">{i + 1}</span>
                          <span>{text}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      className="hm-btn primary full"
                      style={{ marginTop: 20 }}
                      onClick={() => setUploadOpen(true)}
                    >
                      上传第一份报告 →
                    </button>
                  </div>
                ) : (
                  <>
                    {/* ---- Hero 摘要条 ---- */}
                    {(() => {
                      const days = daysSince(summary.latest_report.report_date);
                      const fillPct = summary.total_count > 0
                        ? Math.round((summary.filled_count / summary.total_count) * 100)
                        : 0;
                      return (
                        <div className="hm-hero-strip">
                          <div className="hm-hero-left">
                            <span className="hm-hero-label">最近体检</span>
                            <span className="hm-hero-date">{summary.latest_report.report_date}</span>
                            <span className="hm-hero-ago">{days} 天前</span>
                          </div>
                          <div className="hm-hero-right">
                            <span className="hm-hero-label">检测完整度</span>
                            <div className="hm-progress-wrap">
                              <div className="hm-progress-bar" style={{ width: `${fillPct}%` }} />
                            </div>
                            <span className="hm-hero-label" style={{ textAlign: 'right' }}>
                              {summary.filled_count}/{summary.total_count} 项
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* ---- 环形仪表盘 + 快速统计 ---- */}
                    <div className="hm-dashboard-row">
                      <div className="hm-ring-card">
                        <RingChart
                          total={summary.filled_count ?? 0}
                          abnormal={summary.abnormal_count ?? 0}
                        />
                        <div className="hm-ring-label" style={{
                          color: (summary.abnormal_count ?? 0) > 0 ? '#fca5a5' : '#6ee7b7',
                        }}>
                          {(summary.abnormal_count ?? 0) > 0
                            ? `⚠ ${summary.abnormal_count} 项异常`
                            : '✓ 指标正常'}
                        </div>
                      </div>
                      <div className="hm-quick-stats">
                        {[
                          { icon: '📅', val: summary.latest_report.report_date, label: '最近体检' },
                          {
                            icon: '📊',
                            val: `${summary.filled_count ?? 0}`,
                            valSuffix: `/${summary.total_count ?? 0}`,
                            label: '已测 / 标准项',
                          },
                          { icon: '📈', val: `${catalog.length}`, label: '可追踪指标' },
                          { icon: '📋', val: `${reports.length}`, label: '体检档案' },
                        ].map((item, i) => (
                          <div key={i} className="hm-quick-stat">
                            <span className="hm-qs-icon">{item.icon}</span>
                            <div className="hm-qs-body">
                              <span className="hm-qs-val">
                                {item.val}
                                {'valSuffix' in item && <small>{(item as any).valSuffix}</small>}
                              </span>
                              <span className="hm-qs-label">{item.label}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* ---- 异常指标（醒目置顶） ---- */}
                    {(summary.abnormal_count ?? 0) > 0 && (
                      <div className="hm-alert-card">
                        <div className="hm-alert-head">
                          <span className="hm-alert-icon">⚠️</span>
                          <div>
                            <h3 className="hm-alert-title">需要关注的异常指标</h3>
                            <span className="hm-alert-sub">
                              最近一次体检发现 {summary.abnormal_count} 项指标超出参考范围
                            </span>
                          </div>
                        </div>
                        <div className="hm-alert-list">
                          {(summary.abnormal as Metric[]).map((m) => {
                            const arrowColor = m.status === 'high' ? '#f59e0b' : m.status === 'low' ? '#3b82f6' : '#ef4444';
                            const arrowChar = m.status === 'high' ? '↑' : m.status === 'low' ? '↓' : '!';
                            return (
                              <div
                                key={m.id}
                                className="hm-alert-item"
                                onClick={() => {
                                  const found = catalog.find((c) => c.code === m.code);
                                  if (found) openMetricDetail(found);
                                  else if (m.code) {
                                    openMetricDetail({
                                      code: m.code,
                                      name: m.name,
                                      name_en: m.name_en,
                                      category: m.category,
                                      category_label: m.category,
                                      unit: m.unit,
                                      reference_range: m.reference_range,
                                      latest: {
                                        id: m.id, report_id: null,
                                        code: m.code || '', name: m.name, name_en: m.name_en,
                                        value: m.value, numeric_value: m.numeric_value,
                                        unit: m.unit, reference_range: m.reference_range,
                                        status: m.status, report_date: m.report_date, detected: true,
                                      },
                                      record_count: 1, chart_point_count: 0,
                                      records: [], trend: [], chart_trend: [],
                                    });
                                  }
                                }}
                              >
                                <span className="hm-alert-name">{m.name}</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span className="hm-alert-val">{m.value}</span>
                                  <span style={{ color: arrowColor, fontWeight: 900, fontSize: 14 }}>{arrowChar}</span>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(summary.abnormal_count ?? 0) === 0 && (summary.filled_count ?? 0) > 0 && (
                      <div className="hm-ok-card">
                        <span className="hm-ok-icon">✨</span>
                        <div>
                          <h3 className="hm-ok-title">各项指标都在正常范围内</h3>
                          <span className="hm-ok-sub">继续保持良好的生活习惯！</span>
                        </div>
                      </div>
                    )}

                    {/* ---- 核心指标卡 ---- */}
                    {coreMetrics.length > 0 && (
                      <div className="hm-core-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <h3 className="hm-section-title" style={{ margin: 0 }}>核心指标一览</h3>
                          <button
                            className="hm-link"
                            style={{ fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                            onClick={() => setTab('metrics')}
                          >
                            全部指标 →
                          </button>
                        </div>
                        <div className="hm-core-grid">
                          {coreMetrics.map((item) => {
                            const st = STATUS_MAP[item.latest?.status || 'unknown'] || STATUS_MAP.unknown;
                            const preview = item.chart_trend?.length > 0 ? item.chart_trend : item.trend || [];
                            const pct = refFillPct(item.latest?.numeric_value ?? null, item.reference_range);
                            return (
                              <button
                                key={item.code}
                                type="button"
                                className="hm-core-card"
                                onClick={() => openMetricDetail(item)}
                              >
                                <div className="hm-core-top">
                                  <span className="hm-core-name">{item.name}</span>
                                  <span className="hm-metric-badge" style={{ color: st.color, background: st.bg }}>
                                    {st.label}
                                  </span>
                                </div>
                                <span className="hm-core-val" style={{ color: st.color }}>
                                  {item.latest?.value}
                                  {item.unit && <small>{item.unit}</small>}
                                </span>
                                <div className="hm-core-trend">
                                  <HealthMetricTrendChart
                                    data={preview}
                                    unit={item.unit}
                                    height={28}
                                    compact
                                    referenceRange={item.reference_range}
                                  />
                                </div>
                                {pct != null && (
                                  <div style={{ width: '100%', height: 4, background: 'rgba(148,163,184,0.1)', borderRadius: 999, overflow: 'hidden', marginTop: 4 }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: st.color, borderRadius: 999, minWidth: 3, opacity: 0.7 }} />
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* ---- 最近报告摘要 ---- */}
                    {reports.length > 0 && (() => {
                      const r = reports[0];
                      const fillPct = r.metrics_total && r.metrics_total > 0
                        ? Math.round((r.metrics_count / r.metrics_total) * 100)
                        : null;
                      return (
                        <div className="hm-last-report-card">
                          <div className="hm-lrc-left">
                            <span style={{ fontSize: 24 }}>📋</span>
                            <div style={{ minWidth: 0 }}>
                              <div className="hm-lrc-title">{r.title || r.file_name}</div>
                              <div className="hm-lrc-meta">
                                {r.report_date} · 已识别 <strong>{r.metrics_count}</strong> 项
                              </div>
                              {fillPct != null && (
                                <div style={{ marginTop: 6 }}>
                                  <div className="hm-progress-wrap">
                                    <div className="hm-progress-bar" style={{ width: `${fillPct}%`, background: 'linear-gradient(90deg,#10b981,#38bdf8)' }} />
                                  </div>
                                  <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.5)', marginTop: 2, display: 'block' }}>
                                    识别率 {fillPct}%
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <button className="hm-btn sm" onClick={() => setTab('reports')}>
                            查看档案
                          </button>
                        </div>
                      );
                    })()}

                    <button className="hm-btn primary full" style={{ marginTop: 8 }} onClick={() => setTab('metrics')}>
                      查看全部指标趋势 →
                    </button>
                  </>
                )}
              </div>
            )}

            {/* ================== 指标 Tab ================== */}
            {tab === 'metrics' && (
              <div className="hm-metrics-tab">
                {catalog.length === 0 && !catalogLoading ? (
                  <div className="hm-empty-card">
                    <span style={{ fontSize: 48 }}>📈</span>
                    <p>暂无体检报告指标</p>
                    <p style={{ fontSize: 13, color: 'rgba(148,163,184,0.6)' }}>
                      上传并解析体检报告后，这里会显示所有检测到的数据
                    </p>
                    <button className="hm-btn primary" style={{ marginTop: 16 }} onClick={() => setUploadOpen(true)}>
                      上传报告
                    </button>
                  </div>
                ) : (
                  <>
                    <section className="hm-metric-focus-summary" aria-label="指标状态摘要">
                      <div>
                        <span className="hm-focus-kicker">健康指标总览</span>
                        <strong>{catalog.length}<small>项已追踪</small></strong>
                      </div>
                      <div className="hm-focus-stat attention">
                        <AlertTriangle size={16} />
                        <span><b>{abnormalCount}</b><small>需关注</small></span>
                      </div>
                      <div className="hm-focus-stat normal">
                        <Activity size={16} />
                        <span><b>{normalCount}</b><small>范围内</small></span>
                      </div>
                    </section>

                    {!metricSearch.trim() && !abnormalOnly && priorityMetrics.length > 0 && (
                      <section className="hm-priority-section">
                        <div className="hm-priority-heading">
                          <div>
                            <span className="hm-focus-kicker">重点监测</span>
                            <h3>关键健康趋势</h3>
                          </div>
                          <span>异常优先 · 最近体检</span>
                        </div>
                        <div className="hm-priority-grid">
                          {priorityMetrics.map((item) => {
                            const st = STATUS_MAP[item.latest?.status || 'unknown'] || STATUS_MAP.unknown;
                            const preview = item.chart_trend?.length > 0 ? item.chart_trend : item.trend || [];
                            const numericTrend = preview.filter((point) => point.value != null);
                            const latestValue = numericTrend[numericTrend.length - 1]?.value;
                            const previousValue = numericTrend[numericTrend.length - 2]?.value;
                            const delta = latestValue != null && previousValue != null ? latestValue - previousValue : null;
                            return (
                              <button
                                key={item.code}
                                type="button"
                                className={`hm-priority-card${isAbnormal(item.latest?.status || 'unknown') ? ' attention' : ''}`}
                                onClick={() => openMetricDetail(item)}
                              >
                                <div className="hm-priority-card-head">
                                  <span>
                                    <b>{item.name}</b>
                                    <small>{item.name_en || item.category_label}</small>
                                  </span>
                                  <i className="hm-metric-badge" style={{ color: st.color, background: st.bg }}>{st.label}</i>
                                </div>
                                <div className="hm-priority-value">
                                  <strong>{item.latest?.value || '--'}</strong>
                                  {item.unit && !String(item.latest?.value || '').includes(item.unit) && <small>{item.unit}</small>}
                                  {delta != null && delta !== 0 && (
                                    <em className={delta > 0 ? 'up' : 'down'}>
                                      {delta > 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}
                                    </em>
                                  )}
                                </div>
                                <HealthMetricTrendChart
                                  data={preview}
                                  unit={item.unit}
                                  height={92}
                                  compact
                                  referenceRange={item.reference_range}
                                />
                                <div className="hm-priority-foot">
                                  <span>参考 {item.reference_range || '暂无范围'}</span>
                                  <ChevronRight size={15} />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    )}

                    <div className="hm-metrics-toolbar">
                      <label className="hm-metric-search">
                        <Search size={15} />
                        <input
                          type="search"
                          placeholder="搜索全部指标"
                          value={metricSearch}
                          onChange={(e) => setMetricSearch(e.target.value)}
                        />
                      </label>
                      <button
                        type="button"
                        className={`hm-filter-btn${abnormalOnly ? ' active' : ''}`}
                        onClick={() => setAbnormalOnly((v) => !v)}
                      >
                        <AlertTriangle size={14} />只看异常
                      </button>
                    </div>

                    {catalogLoading ? (
                      <div className="hm-loading" style={{ padding: 40 }}>
                        <div className="hm-spinner" />
                      </div>
                    ) : filteredCatalog.length === 0 ? (
                      <div className="hm-empty-card" style={{ marginTop: 16 }}>
                        <p>未找到匹配的指标</p>
                      </div>
                    ) : (
                      <section className="hm-more-metrics">
                        <button
                          type="button"
                          className="hm-more-toggle"
                          onClick={() => setShowMoreMetrics((value) => !value)}
                          aria-expanded={shouldShowSecondary}
                        >
                          <span>
                            <b>{metricSearch.trim() || abnormalOnly ? '筛选结果' : '更多健康指标'}</b>
                            <small>
                              {metricSearch.trim() || abnormalOnly
                                ? `找到 ${filteredCatalog.length} 项`
                                : `${secondaryCatalog.length} 项次要数据，按分类查看`}
                            </small>
                          </span>
                          {shouldShowSecondary ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>

                        {shouldShowSecondary && (
                          <div className="hm-secondary-groups">
                            {Object.entries(metricSearch.trim() || abnormalOnly ? filteredGrouped : secondaryGrouped).map(([cat, items]) => {
                              const catAbnormalCount = items.filter((item) => isAbnormal(item.latest?.status || 'unknown')).length;
                              return (
                                <section key={cat} className="hm-secondary-group">
                                  <header>
                                    <span><b>{cat}</b><small>{items.length} 项</small></span>
                                    {catAbnormalCount > 0 && <em>{catAbnormalCount} 项异常</em>}
                                  </header>
                                  <div>
                                    {items.map((item) => {
                                      const st = STATUS_MAP[item.latest?.status || 'unknown'] || STATUS_MAP.unknown;
                                      return (
                                        <button key={item.code} type="button" onClick={() => openMetricDetail(item)}>
                                          <span className="hm-secondary-name"><b>{item.name}</b><small>{item.reference_range ? `参考 ${item.reference_range}` : item.name_en}</small></span>
                                          <span className="hm-secondary-value">
                                            <b>{item.latest?.value || '--'}</b>
                                            {item.unit && !String(item.latest?.value || '').includes(item.unit) && <small>{item.unit}</small>}
                                          </span>
                                          <i className="hm-metric-badge" style={{ color: st.color, background: st.bg }}>{st.label}</i>
                                          <ChevronRight size={15} />
                                        </button>
                                      );
                                    })}
                                  </div>
                                </section>
                              );
                            })}
                          </div>
                        )}
                      </section>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ================== 报告 Tab ================== */}
            {tab === 'reports' && (
              <div className="hm-reports">
                {reports.length === 0 ? (
                  <div className="hm-empty-card">
                    <p>暂无体检报告</p>
                    <button className="hm-btn primary" style={{ marginTop: 12 }} onClick={() => setUploadOpen(true)}>
                      上传报告
                    </button>
                  </div>
                ) : (
                  reports.map((r, idx) => {
                    const rs = REPORT_STATUS_MAP[r.status] || REPORT_STATUS_MAP.pending;
                    const fillPct = r.metrics_total && r.metrics_total > 0
                      ? Math.round((r.metrics_count / r.metrics_total) * 100)
                      : null;
                    const isFeatured = idx === 0;
                    return (
                      <div key={r.id} className={`hm-report-card${isFeatured ? ' hm-report-featured' : ' hm-report-compact'}`}>
                        <div className="hm-report-top" onClick={() => r.status === 'done' && openReportDetail(r)}>
                          <div>
                            {isFeatured && (
                              <div className="hm-report-badge-row">
                                <span className="hm-report-latest-tag">最新</span>
                                <span className="hm-report-status" style={{ color: rs.color }}>{rs.label}</span>
                              </div>
                            )}
                            <div className={isFeatured ? 'hm-report-title' : 'hm-report-title-sm'}>
                              {r.title || r.file_name}
                            </div>
                            <div className={isFeatured ? 'hm-report-meta' : 'hm-report-meta-sm'}>
                              📅 {r.report_date} · 📎 {r.file_name}
                            </div>
                          </div>
                          {!isFeatured && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                              {r.status === 'done' && <span style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)' }}>{r.metrics_count} 项</span>}
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: rs.color, display: 'inline-block' }} />
                            </div>
                          )}
                          {isFeatured && (
                            <span className="hm-report-status" style={{ color: rs.color }}>{rs.label}</span>
                          )}
                        </div>
                        {isFeatured && r.status === 'done' && (
                          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(148,163,184,0.1)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'rgba(148,163,184,0.7)', marginBottom: 4 }}>
                              <span>已识别 <strong style={{ color: '#f1f5f9' }}>{r.metrics_count}</strong> 项</span>
                              <button className="hm-link" onClick={() => openReportDetail(r)}>查看详情 →</button>
                            </div>
                            {fillPct != null && (
                              <>
                                <div className="hm-progress-wrap">
                                  <div className="hm-progress-bar" style={{ width: `${fillPct}%`, background: 'linear-gradient(90deg,#10b981,#38bdf8)' }} />
                                </div>
                                <span style={{ fontSize: 10, color: 'rgba(148,163,184,0.4)', marginTop: 3, display: 'block' }}>
                                  识别率 {fillPct}%
                                </span>
                              </>
                            )}
                          </div>
                        )}
                        {!isFeatured && r.status === 'done' && (
                          <button className="hm-link" style={{ fontSize: 12, marginTop: 4, textAlign: 'left' }} onClick={() => openReportDetail(r)}>
                            查看详情 →
                          </button>
                        )}
                        <div className="hm-report-actions">
                          {r.status === 'failed' && (
                            <button className="hm-btn sm" onClick={() => handleReparse(r.id)}>重新解析</button>
                          )}
                          <button className="hm-btn sm danger" onClick={() => handleDelete(r.id)}>删除</button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* 报告详情 overlay */}
      {selectedReport && (
        <div className="hm-overlay" onClick={() => { setSelectedReport(null); setReportDetail(null); }}>
          <div className="hm-detail-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="hm-detail-head">
              <h2>{selectedReport.title}</h2>
              <button className="hm-close" onClick={() => { setSelectedReport(null); setReportDetail(null); }}>✕</button>
            </div>
            {detailLoading ? (
              <div className="hm-loading"><div className="hm-spinner" /></div>
            ) : (
              reportDetail &&
              Object.entries(reportDetail.grouped).map(([cat, items]) => (
                <div key={cat} className="hm-section">
                  <h3 className="hm-section-title">{cat}</h3>
                  <div className="hm-metrics-list">
                    {(items as Metric[]).map((m) => {
                      const detected = !!(m.value && String(m.value).trim());
                      if (!detected) return null;
                      return (
                        <div key={m.id} className="hm-metric-row"
                          onClick={() => {
                            const found = catalog.find((c) => c.code === m.code);
                            if (found) { setSelectedReport(null); setReportDetail(null); openMetricDetail(found); }
                          }}
                        >
                          <span className="hm-metric-name">{m.name}</span>
                          <span className="hm-metric-value">{m.value}</span>
                          <span className="hm-mc-arrow">趋势 →</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* 上传 Dialog overlay */}
      {uploadOpen && (
        <div className="hm-overlay" onClick={() => setUploadOpen(false)}>
          <div className="hm-upload-dialog-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="hm-detail-head">
              <h2>上传体检报告</h2>
              <button className="hm-close" onClick={() => setUploadOpen(false)}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: 'rgba(148,163,184,0.65)', margin: '0 0 16px' }}>
              支持 JPG / PNG / PDF / TXT。解析后每项指标单独入库，可查看历史趋势图。
            </p>
            <div className="hm-field">
              <label>选择文件</label>
              <input type="file" ref={fileRef} accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt" className="hm-file-input" />
            </div>
            <div className="hm-field">
              <label>体检日期</label>
              <input type="date" value={uploadDate} onChange={(e) => setUploadDate(e.target.value)} className="hm-input" />
            </div>
            <div className="hm-field">
              <label>标题（可选）</label>
              <input type="text" value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} className="hm-input" placeholder="如：2025 年度体检" />
            </div>
            {msg && <div className={`hm-toast ${msg.type}`} style={{ marginBottom: 12 }}>{msg.text}</div>}
            <button className="hm-btn primary full" onClick={handleUpload} disabled={uploading}>
              {uploading ? '上传中…' : '上传并解析'}
            </button>
          </div>
        </div>
      )}

      <HmStyles />
    </div>
  );
};

function HmStyles() {
  return (
    <style>{`
      .hm-root {
        min-height: 100vh;
        min-height: 100dvh;
        background: #030712;
        background-image: radial-gradient(circle at 50% 0%, #1e1b4b 0%, #030712 40%);
        color: #f1f5f9;
        padding-bottom: calc(80px + var(--safe-area-inset-bottom, 0px));
      }
      .hm-metric-detail-root { padding-bottom: var(--safe-area-inset-bottom, 16px); }
      .hm-header {
        min-height: 72px;
        display: flex; align-items: center; justify-content: space-between; gap: 16px;
        padding: calc(12px + var(--safe-area-inset-top, 0px)) 16px 10px;
        position: sticky; top: 0; z-index: 20;
        background: linear-gradient(180deg, rgba(3,7,18,0.95) 0%, rgba(3,7,18,0) 100%);
      }
      .hm-back {
        flex-shrink: 0;
        width: 34px; height: 34px; border-radius: 50%;
        border: 1px solid rgba(148,163,184,0.15);
        background: rgba(15,23,42,0.6); color: #cbd5e1;
        display: flex; align-items: center; justify-content: center;
        cursor: pointer; font-family: inherit;
        -webkit-tap-highlight-color: transparent;
      }
      .hm-back:active { transform: scale(0.9); }
      .hm-page-title { font-size: 17px; font-weight: 800; margin: 0; text-align: center; flex: 1; }
      .hm-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 160px; }

      /* 浮动上传按钮 */
      .hm-fab-upload {
        width: 36px; height: 36px; border-radius: 50%; border: none;
        background: linear-gradient(135deg, rgba(6,182,212,0.85), rgba(59,130,246,0.85));
        color: #fff; font-size: 20px; font-weight: 700; cursor: pointer;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 4px 14px rgba(6,182,212,0.4);
        -webkit-tap-highlight-color: transparent;
        flex-shrink: 0;
      }
      .hm-fab-upload:active { transform: scale(0.9); }

      .hm-toast {
        margin: 0 16px 12px; padding: 10px 14px; border-radius: 12px; font-size: 13px; font-weight: 600;
      }
      .hm-toast.ok { background: rgba(16,185,129,0.12); color: #6ee7b7; border: 1px solid rgba(16,185,129,0.2); }
      .hm-toast.err { background: rgba(244,63,94,0.12); color: #fca5a5; border: 1px solid rgba(244,63,94,0.2); }

      .hm-body { padding: 0 16px; max-width: 600px; margin: 0 auto; }
      .hm-metric-detail-body { padding-bottom: 24px; }

      .hm-loading { display: flex; flex-direction: column; align-items: center; padding: 60px 20px; gap: 12px; }
      .hm-spinner {
        width: 32px; height: 32px;
        border: 3px solid rgba(56,189,248,0.2); border-top-color: #38bdf8;
        border-radius: 50%; animation: hmSpin 0.8s linear infinite;
      }
      @keyframes hmSpin { to { transform: rotate(360deg); } }

      .hm-empty-card {
        text-align: center; padding: 48px 20px;
        background: rgba(15,23,42,0.5); border: 1px solid rgba(148,163,184,0.1); border-radius: 20px;
      }
      .hm-empty-card p { color: rgba(148,163,184,0.6); margin: 12px 0 0; }

      /* Onboarding 引导卡 */
      .hm-onboarding-card {
        background: linear-gradient(135deg, rgba(30,41,59,0.85), rgba(15,23,42,0.92));
        border: 1px solid rgba(6,182,212,0.2);
        border-radius: 24px; padding: 32px 20px;
      }
      .hm-onboarding-steps { display: flex; flex-direction: column; gap: 12px; }
      .hm-onboarding-step { display: flex; align-items: center; gap: 12px; font-size: 14px; color: rgba(148,163,184,0.8); }
      .hm-step-num {
        display: flex; align-items: center; justify-content: center;
        width: 26px; height: 26px; border-radius: 50%;
        background: rgba(6,182,212,0.15); border: 1px solid rgba(6,182,212,0.3);
        color: #7dd3fc; font-size: 12px; font-weight: 800; flex-shrink: 0;
      }

      /* Hero 摘要条 */
      .hm-hero-strip {
        display: flex; align-items: center; justify-content: space-between; gap: 16px;
        padding: 14px 16px; background: rgba(15,23,42,0.55);
        border: 1px solid rgba(148,163,184,0.1); border-radius: 16px; margin-bottom: 14px;
      }
      .hm-hero-left, .hm-hero-right { display: flex; flex-direction: column; gap: 4px; }
      .hm-hero-right { min-width: 130px; }
      .hm-hero-label { font-size: 10px; color: rgba(148,163,184,0.6); font-weight: 600; }
      .hm-hero-date { font-size: 15px; font-weight: 800; }
      .hm-hero-ago { font-size: 11px; color: rgba(125,211,252,0.7); }

      /* 进度条 */
      .hm-progress-wrap {
        width: 100%; height: 6px; background: rgba(148,163,184,0.12);
        border-radius: 999px; overflow: hidden;
      }
      .hm-progress-bar {
        height: 100%; background: linear-gradient(90deg, #0ea5e9, #38bdf8);
        border-radius: 999px; transition: width 0.6s cubic-bezier(0.4,0,0.2,1);
      }

      /* 仪表盘行 */
      .hm-dashboard-row { display: flex; gap: 12px; align-items: stretch; margin-bottom: 14px; }
      .hm-ring-card {
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        background: rgba(15,23,42,0.65); border: 1px solid rgba(148,163,184,0.1);
        border-radius: 20px; padding: 14px; min-width: 108px; flex-shrink: 0; gap: 6px;
      }
      .hm-ring-label { font-size: 11px; font-weight: 700; text-align: center; }
      .hm-quick-stats { display: flex; flex-direction: column; gap: 7px; flex: 1; }
      .hm-quick-stat {
        display: flex; align-items: center; gap: 10px;
        background: rgba(15,23,42,0.5); border: 1px solid rgba(148,163,184,0.08);
        border-radius: 13px; padding: 9px 12px; flex: 1;
      }
      .hm-qs-icon { font-size: 16px; flex-shrink: 0; }
      .hm-qs-body { display: flex; flex-direction: column; gap: 1px; }
      .hm-qs-val { font-size: 15px; font-weight: 900; line-height: 1; }
      .hm-qs-val small { font-size: 11px; font-weight: 600; color: rgba(148,163,184,0.6); margin-left: 1px; }
      .hm-qs-label { font-size: 10px; color: rgba(148,163,184,0.55); font-weight: 600; }

      /* 核心指标 */
      .hm-core-section { margin-bottom: 16px; }
      .hm-core-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
      .hm-core-card {
        display: flex; flex-direction: column; padding: 13px;
        background: rgba(15,23,42,0.7); border: 1px solid rgba(148,163,184,0.1);
        border-radius: 18px; cursor: pointer; font-family: inherit; color: inherit;
        text-align: left; -webkit-tap-highlight-color: transparent;
        transition: border-color 0.15s;
      }
      .hm-core-card:active { border-color: rgba(56,189,248,0.35); }
      .hm-core-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; }
      .hm-core-name { font-size: 11px; font-weight: 700; color: rgba(148,163,184,0.8); }
      .hm-core-val { font-size: 20px; font-weight: 900; margin-bottom: 4px; }
      .hm-core-val small { font-size: 10px; font-weight: 500; margin-left: 2px; opacity: 0.7; }
      .hm-core-trend { opacity: 0.85; margin-bottom: 2px; }

      /* 最近报告摘要卡 */
      .hm-last-report-card {
        display: flex; align-items: center; justify-content: space-between; gap: 12px;
        padding: 14px 16px; background: rgba(15,23,42,0.55);
        border: 1px solid rgba(148,163,184,0.1); border-radius: 16px; margin-bottom: 14px;
      }
      .hm-lrc-left { display: flex; align-items: flex-start; gap: 10px; flex: 1; min-width: 0; }
      .hm-lrc-title { font-size: 14px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .hm-lrc-meta { font-size: 11px; color: rgba(148,163,184,0.6); margin-top: 3px; }
      .hm-lrc-meta strong { color: #f1f5f9; }

      /* 指标工作台 */
      .hm-body-wide { max-width: 920px; }
      .hm-metric-focus-summary {
        display: grid; grid-template-columns: minmax(0, 1fr) 150px 150px;
        min-height: 82px; margin-bottom: 18px;
        background: #0b1220; border: 1px solid rgba(148,163,184,0.14); border-radius: 8px;
        overflow: hidden;
      }
      .hm-metric-focus-summary > div {
        display: flex; align-items: center; gap: 10px; padding: 16px 18px;
        border-right: 1px solid rgba(148,163,184,0.1);
      }
      .hm-metric-focus-summary > div:last-child { border-right: 0; }
      .hm-metric-focus-summary > div:first-child { flex-direction: column; align-items: flex-start; justify-content: center; gap: 3px; }
      .hm-focus-kicker { font-size: 10px; color: #64748b; font-weight: 700; }
      .hm-metric-focus-summary > div:first-child strong { font-size: 26px; line-height: 1; }
      .hm-metric-focus-summary strong small { font-size: 11px; color: #64748b; font-weight: 600; margin-left: 6px; }
      .hm-focus-stat svg { flex-shrink: 0; }
      .hm-focus-stat span { display: flex; flex-direction: column; gap: 2px; }
      .hm-focus-stat b { font-size: 20px; line-height: 1; }
      .hm-focus-stat small { font-size: 10px; color: #64748b; font-weight: 600; }
      .hm-focus-stat.attention { color: #fbbf24; }
      .hm-focus-stat.normal { color: #34d399; }

      .hm-priority-section { margin-bottom: 18px; }
      .hm-priority-heading {
        display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin-bottom: 10px;
      }
      .hm-priority-heading h3 { margin: 3px 0 0; font-size: 16px; }
      .hm-priority-heading > span { font-size: 10px; color: #64748b; }
      .hm-priority-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
      .hm-priority-card {
        min-width: 0; padding: 14px; text-align: left; color: inherit; font-family: inherit;
        background: #0b1220; border: 1px solid rgba(148,163,184,0.12); border-radius: 8px;
        cursor: pointer;
      }
      .hm-priority-card.attention { border-left: 3px solid #f59e0b; padding-left: 12px; }
      .hm-priority-card:hover { border-color: rgba(56,189,248,0.34); }
      .hm-priority-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
      .hm-priority-card-head > span { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
      .hm-priority-card-head b { font-size: 14px; }
      .hm-priority-card-head small { color: #64748b; font-size: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .hm-priority-value { min-height: 35px; display: flex; align-items: baseline; gap: 4px; margin: 10px 0 2px; }
      .hm-priority-value strong { font-size: 23px; }
      .hm-priority-value small { color: #94a3b8; font-size: 10px; }
      .hm-priority-value em { margin-left: auto; font-size: 10px; font-style: normal; font-weight: 700; }
      .hm-priority-value em.up { color: #f59e0b; }
      .hm-priority-value em.down { color: #38bdf8; }
      .hm-priority-foot {
        display: flex; align-items: center; justify-content: space-between; gap: 8px;
        padding-top: 8px; border-top: 1px solid rgba(148,163,184,0.08);
        color: #64748b; font-size: 10px;
      }

      .hm-metrics-toolbar { display: flex; gap: 8px; align-items: center; margin-bottom: 10px; }
      .hm-metric-search {
        min-width: 0; flex: 1; display: flex; align-items: center; gap: 8px;
        height: 38px; padding: 0 11px; box-sizing: border-box;
        border: 1px solid rgba(148,163,184,0.14); background: #0b1220; border-radius: 8px;
        color: #64748b;
      }
      .hm-metric-search:focus-within { border-color: rgba(56,189,248,0.42); color: #7dd3fc; }
      .hm-metric-search input {
        width: 100%; min-width: 0; border: 0; outline: 0; background: transparent;
        color: #e2e8f0; font: inherit; font-size: 12px;
      }
      .hm-filter-btn {
        flex-shrink: 0; display: inline-flex; align-items: center; gap: 4px;
        height: 38px; padding: 0 11px; border-radius: 8px;
        border: 1px solid rgba(148,163,184,0.15); background: #0b1220;
        color: rgba(148,163,184,0.75); font-size: 11px; font-weight: 700;
        cursor: pointer; white-space: nowrap; font-family: inherit;
        transition: background 0.15s, border-color 0.15s, color 0.15s;
      }
      .hm-filter-btn.active {
        background: rgba(239,68,68,0.12); border-color: rgba(239,68,68,0.3); color: #fca5a5;
      }

      .hm-more-metrics { border: 1px solid rgba(148,163,184,0.12); border-radius: 8px; overflow: hidden; background: #0b1220; }
      .hm-more-toggle {
        width: 100%; min-height: 58px; padding: 11px 14px;
        display: flex; align-items: center; justify-content: space-between; gap: 12px;
        border: 0; background: transparent; color: inherit; font-family: inherit; cursor: pointer; text-align: left;
      }
      .hm-more-toggle > span { display: flex; flex-direction: column; gap: 3px; }
      .hm-more-toggle b { font-size: 13px; }
      .hm-more-toggle small { color: #64748b; font-size: 10px; }
      .hm-secondary-groups { border-top: 1px solid rgba(148,163,184,0.1); }
      .hm-secondary-group + .hm-secondary-group { border-top: 1px solid rgba(148,163,184,0.1); }
      .hm-secondary-group > header {
        min-height: 40px; padding: 0 14px; display: flex; align-items: center; justify-content: space-between; gap: 10px;
        background: rgba(15,23,42,0.55);
      }
      .hm-secondary-group > header span { display: flex; align-items: baseline; gap: 7px; }
      .hm-secondary-group > header b { font-size: 11px; }
      .hm-secondary-group > header small { color: #64748b; font-size: 9px; }
      .hm-secondary-group > header em { color: #fbbf24; font-size: 9px; font-style: normal; }
      .hm-secondary-group > div > button {
        width: 100%; min-height: 54px; padding: 8px 14px;
        display: grid; grid-template-columns: minmax(0, 1fr) minmax(90px, auto) auto 16px;
        align-items: center; gap: 10px; border: 0; border-top: 1px solid rgba(148,163,184,0.07);
        background: transparent; color: inherit; font-family: inherit; text-align: left; cursor: pointer;
      }
      .hm-secondary-group > div > button:hover { background: rgba(30,41,59,0.45); }
      .hm-secondary-name, .hm-secondary-value { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
      .hm-secondary-name b { font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .hm-secondary-name small { color: #64748b; font-size: 9px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .hm-secondary-value { text-align: right; }
      .hm-secondary-value b { font-size: 12px; }
      .hm-secondary-value small { color: #64748b; font-size: 9px; }

      @media (max-width: 640px) {
        .hm-metric-focus-summary { grid-template-columns: 1fr 92px 92px; }
        .hm-metric-focus-summary > div { padding: 12px; }
        .hm-priority-grid { grid-template-columns: 1fr; }
        .hm-priority-card { padding: 13px; }
        .hm-priority-card.attention { padding-left: 11px; }
        .hm-secondary-group > div > button { grid-template-columns: minmax(0, 1fr) auto auto 15px; gap: 7px; }
        .hm-secondary-name small { max-width: 145px; }
      }

      .hm-metric-hero {
        background: linear-gradient(135deg, rgba(30,58,138,0.4), rgba(15,23,42,0.8));
        border: 1px solid rgba(56,189,248,0.2); border-radius: 20px; padding: 18px; margin-bottom: 16px;
      }
      .hm-metric-hero-top { display: flex; justify-content: space-between; gap: 12px; }
      .hm-metric-hero-name { font-size: 20px; font-weight: 900; }
      .hm-metric-hero-en { font-size: 12px; color: rgba(148,163,184,0.7); margin-top: 2px; }
      .hm-metric-hero-code { font-size: 11px; color: rgba(125,211,252,0.7); margin-top: 6px; font-family: ui-monospace, monospace; }
      .hm-metric-hero-right { text-align: right; }
      .hm-metric-hero-value { font-size: 26px; font-weight: 900; color: #7dd3fc; }
      .hm-metric-hero-unit { font-size: 12px; color: rgba(148,163,184,0.65); }
      .hm-metric-hero-meta {
        display: flex; flex-wrap: wrap; gap: 10px; margin-top: 14px; padding-top: 12px;
        border-top: 1px solid rgba(148,163,184,0.12); font-size: 12px; color: rgba(148,163,184,0.75);
      }

      .hm-chart-card {
        background: rgba(15,23,42,0.6); border: 1px solid rgba(148,163,184,0.1);
        border-radius: 18px; padding: 16px; margin-bottom: 16px;
      }

      .hm-records-hint { font-size: 12px; color: rgba(148,163,184,0.55); margin: -6px 0 12px; }
      .hm-records-list { display: flex; flex-direction: column; gap: 8px; }
      .hm-record-card {
        padding: 14px; border-radius: 14px; background: rgba(30,41,59,0.45);
        border: 1px solid rgba(148,163,184,0.08);
      }
      .hm-record-top-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
      .hm-record-date { font-size: 12px; color: rgba(148,163,184,0.7); }
      .hm-record-delta { font-size: 11px; font-weight: 700; }
      .hm-record-val { font-size: 18px; font-weight: 800; margin-bottom: 6px; }
      .hm-record-ref { font-size: 11px; color: rgba(100,116,139,0.7); margin-top: 6px; }

      .hm-summary-stats { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 20px; }
      .hm-stat-card {
        background: rgba(15,23,42,0.7); border: 1px solid rgba(148,163,184,0.1);
        border-radius: 18px; padding: 14px 10px; text-align: center;
        display: flex; flex-direction: column; align-items: center; gap: 4px;
      }
      .hm-stat-icon { font-size: 20px; }
      .hm-stat-val { font-size: 18px; font-weight: 900; }
      .hm-stat-label { font-size: 10px; color: rgba(148,163,184,0.65); font-weight: 600; }

      .hm-section { margin-bottom: 16px; }
      .hm-section-title {
        font-size: 14px; font-weight: 800; margin: 0 0 10px 2px;
        padding-bottom: 6px; border-bottom: 1px solid rgba(148,163,184,0.1);
      }

      .hm-metrics-list { display: flex; flex-direction: column; gap: 6px; }
      .hm-metric-row {
        display: flex; align-items: center; justify-content: space-between; gap: 10px;
        padding: 12px 14px; border-radius: 14px; background: rgba(15,23,42,0.5);
        border: 1px solid rgba(148,163,184,0.07); cursor: pointer;
      }
      .hm-metric-row.abnormal { border-color: rgba(239,68,68,0.2); }
      .hm-metric-name { font-size: 14px; font-weight: 700; }
      .hm-metric-value { font-size: 15px; font-weight: 800; }
      .hm-metric-badge {
        padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 800;
      }

      .hm-reports { display: flex; flex-direction: column; gap: 12px; }
      .hm-report-card {
        background: rgba(15,23,42,0.7); border: 1px solid rgba(148,163,184,0.1);
        border-radius: 18px; padding: 16px;
      }
      .hm-report-featured {
        border-color: rgba(6,182,212,0.2);
        background: linear-gradient(135deg, rgba(6,182,212,0.06), rgba(15,23,42,0.8));
      }
      .hm-report-compact { padding: 12px 14px; }
      .hm-report-badge-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
      .hm-report-latest-tag {
        display: inline-block; padding: 2px 8px; border-radius: 999px;
        background: rgba(6,182,212,0.15); border: 1px solid rgba(6,182,212,0.3);
        color: #7dd3fc; font-size: 10px; font-weight: 800;
      }
      .hm-report-top { display: flex; justify-content: space-between; cursor: pointer; }
      .hm-report-title { font-size: 15px; font-weight: 700; }
      .hm-report-title-sm { font-size: 13px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }
      .hm-report-meta { font-size: 12px; color: rgba(148,163,184,0.6); margin-top: 4px; }
      .hm-report-meta-sm { font-size: 11px; color: rgba(148,163,184,0.5); }
      .hm-report-status { font-size: 12px; font-weight: 800; }
      .hm-report-actions { display: flex; gap: 8px; margin-top: 10px; }
      .hm-link { background: none; border: none; color: #7dd3fc; font-weight: 600; cursor: pointer; font-family: inherit; }

      .hm-alert-card {
        background: linear-gradient(135deg, rgba(239,68,68,0.1), rgba(15,23,42,0.8));
        border: 1px solid rgba(239,68,68,0.25); border-radius: 18px; padding: 16px; margin-bottom: 14px;
      }
      .hm-alert-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
      .hm-alert-icon { font-size: 28px; }
      .hm-alert-title { margin: 0; font-size: 15px; font-weight: 800; color: #fca5a5; }
      .hm-alert-sub { font-size: 11px; color: rgba(148,163,184,0.7); }
      .hm-alert-list { display: flex; flex-direction: column; gap: 6px; }
      .hm-alert-item {
        display: flex; justify-content: space-between; align-items: center;
        padding: 10px 14px; border-radius: 12px;
        background: rgba(15,23,42,0.55); border: 1px solid rgba(239,68,68,0.15); cursor: pointer;
      }
      .hm-alert-name { font-size: 13px; font-weight: 600; }
      .hm-alert-val { font-size: 14px; font-weight: 800; color: #fca5a5; }

      .hm-ok-card {
        display: flex; align-items: center; gap: 14px; padding: 16px;
        background: linear-gradient(135deg, rgba(16,185,129,0.1), rgba(15,23,42,0.78));
        border: 1px solid rgba(16,185,129,0.2); border-radius: 18px; margin-bottom: 14px;
      }
      .hm-ok-icon { font-size: 32px; }
      .hm-ok-title { margin: 0 0 2px; font-size: 15px; color: #6ee7b7; font-weight: 800; }
      .hm-ok-sub { font-size: 11px; color: rgba(148,163,184,0.7); }

      .hm-btn {
        padding: 10px 18px; border-radius: 12px; font-size: 14px; font-weight: 700;
        border: none; cursor: pointer; font-family: inherit;
      }
      .hm-btn.primary { background: linear-gradient(135deg, #0369a1, #38bdf8); color: #0f172a; }
      .hm-btn.full { width: 100%; }
      .hm-btn.sm { padding: 6px 14px; font-size: 12px; background: rgba(30,41,59,0.8); color: #cbd5e1; border: 1px solid rgba(148,163,184,0.2); }
      .hm-btn.danger { background: rgba(239,68,68,0.15); color: #fca5a5; }
      .hm-btn:disabled { opacity: 0.5; }

      /* Upload form fields */
      .hm-field { margin-bottom: 14px; }
      .hm-field label { display: block; font-size: 12px; font-weight: 700; color: rgba(148,163,184,0.8); margin-bottom: 6px; }
      .hm-input {
        width: 100%; padding: 10px 12px; border-radius: 12px;
        border: 1px solid rgba(148,163,184,0.2); background: rgba(15,23,42,0.9);
        color: #f1f5f9; font-size: 14px; font-family: inherit; box-sizing: border-box;
      }
      .hm-file-input { width: 100%; color: rgba(203,213,225,0.8); font-size: 14px; }

      .hm-overlay {
        position: fixed; inset: 0; z-index: 1200;
        background: rgba(2,6,23,0.85); backdrop-filter: blur(12px);
        display: flex; align-items: center; justify-content: center; padding: 16px;
      }
      .hm-detail-sheet {
        width: 100%; max-width: 560px; max-height: 85vh; overflow-y: auto;
        background: rgba(10,15,36,0.97); border: 1px solid rgba(148,163,184,0.12);
        border-radius: 24px; padding: 22px;
      }
      .hm-upload-dialog-sheet {
        width: 100%; max-width: 420px;
        background: rgba(10,15,36,0.97); border: 1px solid rgba(148,163,184,0.12);
        border-radius: 24px; padding: 22px;
      }
      .hm-detail-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
      .hm-detail-head h2 { margin: 0; font-size: 17px; font-weight: 800; }
      .hm-close {
        width: 34px; height: 34px; border-radius: 999px;
        border: 1px solid rgba(148,163,184,0.2); background: rgba(30,41,59,0.7);
        color: rgba(148,163,184,0.8); cursor: pointer;
      }
    `}</style>
  );
}

export default HealthManager;
